'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBusinessStats } from '@/hooks/use-business-stats';
import {
  ArrowLeft,
  Handshake,
  ChartColumnIncreasing,
  FileHeartIcon,
  ClipboardList,
} from 'lucide-react';
import type { MetricType } from './types';
import { motion, AnimatePresence } from 'framer-motion';
import { HandsPrayingIcon } from '@phosphor-icons/react';
import { BusinessFeedView, type FilterOption } from './components/business-feed-view';
import { UpcomingEventsSection } from './components/UpcomingEventsSection';
import { useCommunityContext } from '@/providers/community-context';
import { ButtonTabs, ButtonTabsList, ButtonTabsTrigger } from '@/components/ui/button-tabs';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import '@aejkatappaja/phantom-ui';

type PageType = 'leads' | 'thank-you' | 'testimonial' | 'meetup';

const getFiltersForPage = (
  page: PageType | null,
  scope: 'community' | 'my-activity'
): { filters: FilterOption[]; defaultFilter: string } => {
  switch (page) {
    case 'leads':
      return scope === 'my-activity'
        ? {
            filters: [
              { id: 'direct', label: 'Received', text: 'Leads that you received directly' },
              { id: 'my-leads', label: 'Given', text: 'Leads that you have posted' },
            ],
            defaultFilter: 'direct',
          }
        : {
            filters: [
              { id: 'all', label: 'All', text: 'All Leads that have been posted' },
              { id: 'my-leads', label: 'My Leads', text: 'Leads that you have posted' },
              { id: 'direct', label: 'Direct', text: 'Leads that you received directly' },
              { id: 'open', label: 'Open', text: "Community's open Leads" },
              { id: 'interested', label: 'Interested', text: 'Leads you have shown your interest' },
            ],
            defaultFilter: 'all',
          };
    case 'thank-you':
      return scope === 'my-activity'
        ? {
            filters: [
              { id: 'my-received', label: 'Received', text: 'Thank You notes that you acquired' },
              { id: 'my-given', label: 'Given', text: 'Thank You notes that you published' },
            ],
            defaultFilter: 'my-received',
          }
        : {
            filters: [
              { id: 'all', label: 'All', text: 'All Thank You notes that have been posted' },
              {
                id: 'my-related',
                label: 'My Related',
                text: 'Thank You notes that you are related to',
              },
              { id: 'my-given', label: 'Given', text: 'Thank You notes that you published' },
              { id: 'my-received', label: 'Received', text: 'Thank You notes that you acquired' },
              { id: 'others', label: 'Others', text: 'Thank You notes that others have published' },
            ],
            defaultFilter: 'all',
          };
    case 'testimonial':
      return scope === 'my-activity'
        ? {
            filters: [
              { id: 'my-received', label: 'Received', text: 'Testimonials that you have received' },
              { id: 'my-given', label: 'Given', text: 'Testimonials that you have given' },
            ],
            defaultFilter: 'my-received',
          }
        : {
            filters: [
              { id: 'all', label: 'All', text: 'All Testimonials that have been posted' },
              { id: 'my-given', label: 'Given', text: 'Testimonials that you have given' },
              { id: 'my-received', label: 'Received', text: 'Testimonials that you have received' },
              { id: 'others', label: 'Others', text: 'Testimonials that others have posted' },
            ],
            defaultFilter: 'all',
          };
    case 'meetup':
      return scope === 'my-activity'
        ? {
            filters: [
              {
                id: 'my-participated',
                label: 'Participated',
                text: 'Meetups that you have participated in',
              },
              { id: 'my-initiated', label: 'Initiated', text: 'Meetups that you have initiated' },
            ],
            defaultFilter: 'my-participated',
          }
        : {
            filters: [
              { id: 'all', label: 'All', text: 'All Meetups that have been posted' },
              { id: 'my-related', label: 'Related', text: 'Meetups that you are related to' },
              { id: 'my-initiated', label: 'Initiated', text: 'Meetups that you have initiated' },
              {
                id: 'my-participated',
                label: 'Participated',
                text: 'Meetups that you have participated in',
              },
            ],
            defaultFilter: 'all',
          };
    default:
      return { filters: [], defaultFilter: '' };
  }
};

const LeadIcon = ({ className }: { className?: string }) => (
  <ChartColumnIncreasing className={className} />
);
const ThankYouIcon = ({ className }: { className?: string }) => (
  <HandsPrayingIcon className={className} />
);
const TestimonialIcon = ({ className }: { className?: string }) => (
  <FileHeartIcon className={className} />
);
const MeetupIcon = ({ className }: { className?: string }) => <Handshake className={className} />;

const TIME_FILTERS = [
  { label: 'Last\nMeeting', value: 'meeting' },
  { label: '1 Month', value: '1month' },
  { label: '1 Year', value: '1year' },
  { label: 'This\nTenure', value: 'currentyear' },
  { label: 'Life\nTime', value: 'all' },
];

const QUICK_TIME_FILTERS = TIME_FILTERS.filter((f) =>
  ['meeting', 'currentyear', 'all'].includes(f.value)
);

export function BusinessView() {
  const [scopeMode, setScopeMode] = useState<'community' | 'my-activity'>(
    (localStorage.getItem('scopeMode') as 'community' | 'my-activity') ?? 'my-activity'
  );
  const savedViewMode = localStorage.getItem('viewMode');
  const [viewMode, setViewMode] = useState<PageType>(
    savedViewMode == null ? 'leads' : (savedViewMode as PageType)
  );
  const [timeFilter, setTimeFilter] = useState<string>(localStorage.getItem('timeFilter') ?? 'all');
  const debouncedSearch = '';
  const [detailPage, setDetailPage] = useState<PageType | null>(null);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  const activeFilterKey = `${detailPage ?? ''}-${scopeMode}`;
  const activeFilter = detailPage
    ? (activeFilters[activeFilterKey] ?? getFiltersForPage(detailPage, scopeMode).defaultFilter)
    : '';
  const setActiveFilter = (filter: string) => {
    if (detailPage) setActiveFilters((prev) => ({ ...prev, [activeFilterKey]: filter }));
  };

  const { communityId } = useParams<{ communityId: string }>();
  const navigate = useNavigate();
  const { community: currentCommunity } = useCommunityContext();
  const detailHistoryPushedRef = useRef(false);

  const closeDetailPage = useCallback((fromPopState = false) => {
    setDetailPage(null);
    if (detailHistoryPushedRef.current) {
      detailHistoryPushedRef.current = false;
      if (!fromPopState) {
        window.history.back();
      }
    }
  }, []);

  const openDetailPage = useCallback((page: PageType) => {
    setDetailPage(page);
    setViewMode(page);
    localStorage.setItem('viewMode', page);
    if (!detailHistoryPushedRef.current) {
      window.history.pushState({ businessActivityDetail: true }, '');
      detailHistoryPushedRef.current = true;
    }
  }, []);

  useEffect(() => {
    const onPopState = () => {
      if (detailHistoryPushedRef.current) {
        detailHistoryPushedRef.current = false;
        setDetailPage(null);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const apiTimeFilter = timeFilter;
  const customDateParams = '';

  const statsScope = scopeMode === 'my-activity' ? 'my' : 'community';

  const {
    stats: metricsData,
    isLoading,
    isRefreshing,
  } = useBusinessStats(communityId!, apiTimeFilter, statsScope, null, null);

  const isStatsInitialLoading = isLoading && !metricsData;
  const isStatsBusy = isStatsInitialLoading || isRefreshing;

  const metrics = metricsData || {
    leads: { given: 0, received: 0 },
    thanks: { given: 0, received: 0, givenAmount: 0, receivedAmount: 0 },
    testimonials: { given: 0, received: 0 },
    meetups: { initiated: 0, participated: 0 },
  };

  const _handleCreate = (type: MetricType) => {
    const routeMap: Record<MetricType, string> = {
      leads: 'lead',
      thanks: 'thank-you',
      testimonials: 'testimonial',
      meetups: 'meetup',
    };
    navigate(`/${communityId}/create/${routeMap[type]}`);
  };

  const meetupsStats = metrics as unknown as {
    meetups: { initiated: number; participated: number };
  };

  const renderViewContent = () => {
    const endpointMap: Record<PageType, string> = {
      leads: `/api/communities/${communityId}/leads?timeFilter=${timeFilter}${customDateParams}`,
      'thank-you': `/api/communities/${communityId}/thank-you?timeFilter=${timeFilter}${customDateParams}`,
      testimonial: `/api/communities/${communityId}/testimonial?timeFilter=${timeFilter}${customDateParams}`,
      meetup: `/api/communities/${communityId}/meetup?timeFilter=${timeFilter}${customDateParams}`,
    };
    const page = detailPage ?? viewMode;
    const endpoint = endpointMap[page];
    const emptyMap: Record<PageType, { title: string; description: string }> = {
      leads: {
        title: 'No leads found',
        description: 'There are no leads matching the selected filter.',
      },
      'thank-you': {
        title: 'No thank you notes found',
        description: 'There are no thank you notes matching the selected filter.',
      },
      testimonial: {
        title: 'No testimonials found',
        description: 'There are no testimonials matching the selected filter.',
      },
      meetup: {
        title: 'No meetups found',
        description: 'There are no meetups matching the selected filter.',
      },
    };
    return (
      <BusinessFeedView
        apiEndpoint={endpoint}
        activeFilter={activeFilter}
        searchQuery={debouncedSearch}
        emptyTitle={emptyMap[page].title}
        emptyDescription={emptyMap[page].description}
      />
    );
  };

  const metricToPage: Record<string, PageType> = {
    leads: 'leads',
    meetups: 'meetup',
    thanks: 'thank-you',
    testimonials: 'testimonial',
  };

  const switchScope = (mode: 'community' | 'my-activity') => {
    setScopeMode(mode);
    localStorage.setItem('scopeMode', mode);
    if (detailPage === null) {
      closeDetailPage();
    }
  };

  const myActivityRows: {
    label: string;
    Icon: React.ComponentType<{ className?: string }>;
    iconBg: string;
    iconColor: string;
    given: number;
    received: number;
    givenAmount?: number;
    receivedAmount?: number;
    givenLabel: string;
    receivedLabel: string;
    metricType: MetricType | null;
  }[] = [
    {
      label: 'Lead',
      Icon: ChartColumnIncreasing,
      iconBg: 'bg-violet-100 dark:bg-violet-950/60',
      iconColor: 'text-violet-600 dark:text-violet-400',
      given: metrics.leads.given,
      received: metrics.leads.received,
      givenLabel: 'Given',
      receivedLabel: 'Received',
      metricType: 'leads',
    },
    {
      label: 'Thank You',
      Icon: HandsPrayingIcon,
      iconBg: 'bg-amber-100 dark:bg-amber-950/60',
      iconColor: 'text-amber-600 dark:text-amber-400',
      given: metrics.thanks.given,
      received: metrics.thanks.received,
      givenAmount: metrics.thanks.givenAmount,
      receivedAmount: metrics.thanks.receivedAmount,
      givenLabel: 'Given',
      receivedLabel: 'Received',
      metricType: 'thanks',
    },
    {
      label: 'Testimonial',
      Icon: FileHeartIcon,
      iconBg: 'bg-rose-100 dark:bg-rose-950/60',
      iconColor: 'text-rose-600 dark:text-rose-400',
      given: metrics.testimonials.given,
      received: metrics.testimonials.received,
      givenLabel: 'Given',
      receivedLabel: 'Received',
      metricType: 'testimonials',
    },
    {
      label: 'Meeting',
      Icon: MeetupIcon,
      iconBg: 'bg-blue-100 dark:bg-blue-950/60',
      iconColor: 'text-blue-600 dark:text-blue-400',
      given: meetupsStats.meetups?.initiated ?? 0,
      received: meetupsStats.meetups?.participated ?? 0,
      givenLabel: 'Initiated',
      receivedLabel: 'Participated',
      metricType: 'meetups',
    },
  ];

  const allActivityRows: {
    label: string;
    Icon: React.ComponentType<{ className?: string }>;
    iconBg: string;
    iconColor: string;
    total: number;
    metricType: MetricType | null;
    enabled: boolean;
  }[] = [
    {
      label: 'Leads',
      Icon: ChartColumnIncreasing,
      iconBg: 'bg-violet-100 dark:bg-violet-950/60',
      iconColor: 'text-violet-600 dark:text-violet-400',
      total: metrics.leads.given + metrics.leads.received,
      metricType: 'leads',
      enabled: !!currentCommunity.isLeadEnabled,
    },
    {
      label: 'Thank You',
      Icon: HandsPrayingIcon,
      iconBg: 'bg-amber-100 dark:bg-amber-950/60',
      iconColor: 'text-amber-600 dark:text-amber-400',
      total: metrics.thanks.given + metrics.thanks.received,
      metricType: 'thanks',
      enabled: !!currentCommunity.isThankYouNoteEnabled,
    },
    {
      label: 'Testimonials',
      Icon: FileHeartIcon,
      iconBg: 'bg-rose-100 dark:bg-rose-950/60',
      iconColor: 'text-rose-600 dark:text-rose-400',
      total: metrics.testimonials.given + metrics.testimonials.received,
      metricType: 'testimonials',
      enabled: !!currentCommunity.isTestimonialEnabled,
    },
    {
      label: 'Meetup',
      Icon: MeetupIcon,
      iconBg: 'bg-blue-100 dark:bg-blue-950/60',
      iconColor: 'text-blue-600 dark:text-blue-400',
      total: (meetupsStats.meetups?.initiated ?? 0) + (meetupsStats.meetups?.participated ?? 0),
      metricType: 'meetups',
      enabled: !!currentCommunity.isMeetupEnabled,
    },
  ];

  const timeFiltersRow = (
    <div className="flex items-center gap-1 sm:gap-3 pr-[8px]">
      {/* Segmented capsule for quick filters */}
      <div className="flex items-center gap-[20px] text-[10px] sm:text-[11px] font-semibold tracking-tighter sm:tracking-normal h-8 sm:h-9">
        {QUICK_TIME_FILTERS.map(({ label, value: v }, idx) => (
          <button
            key={v}
            onClick={() => {
              setTimeFilter(v);
              localStorage.setItem('timeFilter', v);
            }}
            className={cn(
              'transition-colors whitespace-pre-line text-center leading-tight pr-[2px]',
              idx < QUICK_TIME_FILTERS.length - 1 &&
                'pr-[9px] border-r border-gray-500 dark:border-gray-400',
              timeFilter === v
                ? 'pr-[9px] border-r border-gray-500 dark:border-gray-400 text-primary font-bold'
                : 'text-gray-600 dark:text-gray-300 hover:text-foreground'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Checklist-styled filter dropdown trigger */}
      <Popover>
        <PopoverTrigger asChild>
          <button className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-lg bg-white text-gray-500 shadow-[3px_0_6px_rgba(0,0,0,0.25)] border border-gray-200 hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800">
            <ClipboardList className="h-5 w-5 sm:h-6 sm:w-6 text-primary/80 dark:text-primary" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-1" align="end">
          {TIME_FILTERS.map(({ label, value: v }) => (
            <button
              key={v}
              onClick={() => {
                setTimeFilter(v);
                localStorage.setItem('timeFilter', v);
              }}
              className={cn(
                'w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted whitespace-pre-line',
                timeFilter === v ? 'font-bold text-primary' : 'font-medium'
              )}
            >
              {label}
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );

  const chromeTabHeader = (
    <div className="flex flex-nowrap items-end justify-between border-b border-border/40 bg-muted/10 overflow-x-auto [&::-webkit-scrollbar]:hidden">
      <div className="flex shrink-0 items-end">
        {(['community', 'my-activity'] as const).map((mode, i) => {
          const isActive = scopeMode === mode;
          const hasLeftSweep = i === 1 && isActive;
          return (
            <button
              key={mode}
              onClick={() => switchScope(mode)}
              className={cn(
                'py-2 sm:py-2.5 h-9 sm:h-10 text-[12px] sm:text-[15px] font-semibold transition-all relative rounded-t-xl sm:rounded-t-2xl',
                'pl-[0.4rem] pr-[0.5rem] sm:pl-6 sm:pr-6',
                isActive
                  ? 'bg-card text-foreground z-10 -mb-px shadow-[0_2px_8px_rgba(0,0,0,0.4)]'
                  : 'bg-[rgb(219,221,220)] dark:bg-gray-800 text-gray-700 dark:text-gray-400 hover:bg-[#b0b3b1] shadow-[0_2px_8px_rgba(0,0,0,0.4)]'
              )}
            >
              {hasLeftSweep && (
                <div
                  className={cn(
                    'absolute bottom-0 -left-2 w-2 h-2 rounded-br-full pointer-events-none',
                    isActive
                      ? 'shadow-[2px_2px_0_2px_hsl(var(--card))]'
                      : 'shadow-[2px_2px_0_2px_rgb(219,221,220)] dark:shadow-[2px_2px_0_2px_rgb(31,41,55)]'
                  )}
                />
              )}
              {mode === 'community' ? 'All Activity' : 'My Activity'}
              <div
                className={cn(
                  'absolute bottom-0 -right-2 w-2 h-2 rounded-bl-full pointer-events-none',
                  isActive
                    ? 'shadow-[-2px_2px_0_2px_hsl(var(--card))]'
                    : 'shadow-[-2px_2px_0_2px_rgb(219,221,220)] dark:shadow-[-2px_2px_0_2px_rgb(31,41,55)]'
                )}
              />
            </button>
          );
        })}
      </div>
      <div className="pb-[0.4rem] sm:pb-2.5 shrink-0">{timeFiltersRow}</div>
    </div>
  );

  if (detailPage !== null) {
    return (
      <motion.div
        key="detail"
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 40 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className="space-y-3"
      >
        <div className="flex items-center gap-2 -mx-4 px-4 py-2.5 bg-muted/50 dark:bg-muted/30 rounded-t-xl">
          <button
            onClick={() => closeDetailPage()}
            className="flex items-center justify-center text-foreground hover:text-muted-foreground transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5 stroke-[2.5]" />
          </button>
          <h2 className="text-xl font-bold text-foreground">Activity</h2>
        </div>

        {/* Same card + chrome-tab header as main view */}
        <div className="overflow-hidden rounded-2xl border border-border/50 -mx-3 w-[calc(100%+1.5rem)] sm:mx-0 sm:w-full">
          {chromeTabHeader}

          {/* ButtonTabs + filter pills live inside the card */}
          <div className="bg-card px-3 py-3">
            <phantom-ui
              loading={isStatsBusy || undefined}
              animation="shimmer"
              reveal={0.2}
              class="block"
            >
              <ButtonTabs
                value={detailPage}
                onValueChange={(v) => {
                  openDetailPage(v as PageType);
                }}
              >
                <ButtonTabsList className="!w-full">
                  {currentCommunity.isLeadEnabled && (
                    <ButtonTabsTrigger
                      value="leads"
                      icon={LeadIcon}
                      badge={metrics.leads.given + metrics.leads.received}
                      badgeTone="amber"
                      className="flex-1 w-auto"
                    >
                      Leads
                    </ButtonTabsTrigger>
                  )}
                  {currentCommunity.isThankYouNoteEnabled && (
                    <ButtonTabsTrigger
                      value="thank-you"
                      icon={ThankYouIcon}
                      badge={metrics.thanks.given + metrics.thanks.received}
                      badgeTone="amber"
                      className="flex-1 w-auto"
                    >
                      Thank You
                    </ButtonTabsTrigger>
                  )}
                  {currentCommunity.isTestimonialEnabled && (
                    <ButtonTabsTrigger
                      value="testimonial"
                      icon={TestimonialIcon}
                      badge={metrics.testimonials.given + metrics.testimonials.received}
                      badgeTone="amber"
                      className="flex-1 w-auto"
                    >
                      Testimonials
                    </ButtonTabsTrigger>
                  )}
                  {currentCommunity.isMeetupEnabled && (
                    <ButtonTabsTrigger
                      value="meetup"
                      icon={MeetupIcon}
                      badge={
                        (meetupsStats.meetups?.initiated ?? 0) +
                        (meetupsStats.meetups?.participated ?? 0)
                      }
                      badgeTone="amber"
                      className="flex-1 w-auto"
                    >
                      Meetup
                    </ButtonTabsTrigger>
                  )}
                </ButtonTabsList>
              </ButtonTabs>
            </phantom-ui>

            {/* Filter pills row */}
            {(() => {
              const { filters } = getFiltersForPage(detailPage, scopeMode);
              return filters.length > 0 ? (
                <div className="mt-3 pt-3 border-t border-border/40 flex items-center w-full">
                  {filters.map((filter, idx) => (
                    <button
                      key={filter.id}
                      onClick={() => setActiveFilter(filter.id)}
                      className={cn(
                        'flex-1 text-center px-2 py-0 text-[13px] leading-snug whitespace-nowrap transition-colors',
                        idx < filters.length - 1 && 'border-r border-gray-300 dark:border-gray-600',
                        activeFilter === filter.id
                          ? 'font-semibold text-foreground'
                          : 'font-medium text-gray-400 dark:text-gray-500 hover:text-foreground'
                      )}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              ) : null;
            })()}
          </div>
        </div>

        {/* Feed content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${detailPage}-${scopeMode}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            {renderViewContent()}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    );
  }

  return (
    <div className="space-y-3">
      {communityId && (
        <UpcomingEventsSection
          communityId={communityId}
          enabled={!!currentCommunity.showUpcomingEventsInSocialDashboard}
        />
      )}
      <h2 className="text-xl font-bold text-foreground px-0.5">Activities</h2>
      <div className="overflow-hidden rounded-2xl border border-border/50 -mx-3 w-[calc(100%+1.5rem)] sm:mx-0 sm:w-full">
        {chromeTabHeader}

        <div className="bg-card">
          {scopeMode === 'my-activity' && (
            <div
              className="flex items-center justify-end gap-6 px-4 pt-2.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground"
              data-shimmer-ignore
            >
              <span className="w-10 text-center">Received</span>
              <span className="w-10 text-center">Given</span>
            </div>
          )}

          <phantom-ui
            loading={isStatsBusy || undefined}
            animation="shimmer"
            reveal={0.2}
            class="block"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={scopeMode}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              >
                {scopeMode === 'my-activity'
                  ? myActivityRows.map(
                      ({ label, Icon, iconBg, iconColor, given, received, metricType }, i) => {
                        const pageType = metricType ? metricToPage[metricType] : null;
                        return (
                          <div
                            key={label}
                            onClick={() => {
                              if (!pageType || isStatsBusy) return;
                              openDetailPage(pageType);
                            }}
                            className={cn(
                              'flex items-center justify-between px-4 py-3.5 cursor-pointer transition-colors hover:bg-muted/40 active:bg-muted/60 shadow-[0_-3px_12px_rgba(0,0,0,0.15)]',
                              i < myActivityRows.length - 1 && 'border-b border-border'
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={cn(
                                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                                  iconBg
                                )}
                              >
                                <Icon className={cn('h-5 w-5', iconColor)} />
                              </div>
                              <span className="text-[15px] font-semibold text-foreground leading-tight">
                                {label}
                              </span>
                            </div>
                            <div className="flex items-center gap-6">
                              <span className="w-10 text-center text-xl font-semibold text-gray-400 dark:text-gray-500">
                                {received}
                              </span>
                              <span className="w-10 text-center text-xl font-semibold text-gray-400 dark:text-gray-500">
                                {given}
                              </span>
                            </div>
                          </div>
                        );
                      }
                    )
                  : allActivityRows
                      .filter((r) => r.enabled)
                      .map(({ label, Icon, iconBg, iconColor, total, metricType }, i, arr) => {
                        const pageType = metricType ? metricToPage[metricType] : null;
                        return (
                          <div
                            key={label}
                            onClick={() => {
                              if (!pageType || isStatsBusy) return;
                              openDetailPage(pageType);
                            }}
                            className={cn(
                              'flex items-center justify-between px-4 py-3.5 cursor-pointer transition-colors hover:bg-muted/40 active:bg-muted/60 shadow-[0_-3px_12px_rgba(0,0,0,0.15)]',
                              i < arr.length - 1 && 'border-b border-border'
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={cn(
                                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                                  iconBg
                                )}
                              >
                                <Icon className={cn('h-5 w-5', iconColor)} />
                              </div>
                              <span className="text-[15px] font-semibold text-foreground leading-tight">
                                {label}
                              </span>
                            </div>
                            <span className="text-2xl font-semibold text-gray-400 dark:text-gray-500">
                              {total}
                            </span>
                          </div>
                        );
                      })}
              </motion.div>
            </AnimatePresence>
          </phantom-ui>
        </div>
      </div>
    </div>
  );
}
